import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '@/components/PageHeader.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import { Field } from '@/components/Field.jsx';
import CustomerSelect from '@/components/customers/CustomerSelect.jsx';
import CustomerModal from '@/components/customers/CustomerModal.jsx';
import ProvinciaSelect from '@/components/geo/ProvinciaSelect.jsx';
import LocalidadSelect from '@/components/geo/LocalidadSelect.jsx';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';
import { cambiarEstadoRetiro, createRetiro, fetchRetiro, fetchRetiroSucursales, fetchRetiros, fetchRetirosCount, updateRetiro } from '@/services/retiros.js';
import { useNavigate } from 'react-router-dom';
import { getClienteById } from '@/services/clients.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';

const ESTADOS = [
  { value: 'TOMADO', label: 'Tomado' },
  { value: 'EN_RECOLECCION', label: 'En recolección' },
  { value: 'RETIRADO', label: 'Retirado' },
  { value: 'CANCELADO', label: 'Cancelado' },
];

const FORMA_PAGO = [
  { value: 'CONTADO DESTINO', label: 'CONTADO DESTINO' },
  { value: 'CONTADO ORIGEN', label: 'CONTADO ORIGEN' },
  { value: 'CTA CTE ORIGEN', label: 'CTA CTE ORIGEN' },
  { value: 'CTA CTE DESTINO', label: 'CTA CTE DESTINO' },
];

const FILTER_DEBOUNCE_MS = 350;
const NUMERIC_FIELDS = ['importe', 'bultos', 'kilosReales', 'mts3', 'kilosAforados', 'valorDeclarado'];
const ID_FIELDS = ['sucursalId', 'remitenteClienteId', 'remitenteProvinciaId', 'remitenteLocalidadId', 'destinatarioClienteId', 'destinatarioProvinciaId', 'destinatarioLocalidadId'];
const DATETIME_FIELDS = [];
const EMPTY_TIME_RANGE = { desde: '08:00', hasta: '18:00' };

const initialForm = () => ({
  sucursalId: '',
  sucursalRetiroLabel: '',
  remitenteClienteId: '',
  remitenteRazonSocial: '',
  remitenteDomicilio: '',
  remitenteCp: '',
  remitenteProvinciaId: '',
  remitenteLocalidadId: '',
  remitenteTelefono: '',
  remitenteContacto: '',
  destinatarioClienteId: '',
  destinatarioRazonSocial: '',
  destinatarioDomicilio: '',
  destinatarioCp: '',
  destinatarioProvinciaId: '',
  destinatarioLocalidadId: '',
  destinatarioTelefono: '',
  destinatarioContacto: '',
  formaPago: '',
  importe: '',
  remitoClientePuntoVenta: '',
  remitoClienteNumero: '',
  remitoCliente: '',
  bultos: '',
  descripcion: '',
  kilosReales: '',
  mts3: '',
  kilosAforados: '',
  valorDeclarado: '',
  observacion: '',
  disponibilidadHoraria: [{ ...EMPTY_TIME_RANGE }],
});
const RETIRO_INPUT_FIELD_NAMES = new Set(Object.keys(initialForm()));

const sanitizeAvailabilityRanges = value => {
  const source = Array.isArray(value)
    ? value
    : typeof value === 'string' && value.trim()
      ? (() => {
          try {
            return JSON.parse(value);
          } catch {
            return [];
          }
        })()
      : [];

  const normalized = source
    .map(range => ({
      desde: typeof range?.desde === 'string' ? range.desde.trim() : '',
      hasta: typeof range?.hasta === 'string' ? range.hasta.trim() : '',
    }))
    .filter(range => range.desde || range.hasta);

  return normalized.length ? normalized : [{ ...EMPTY_TIME_RANGE }];
};

const toNullableString = value => {
  if (value == null) return '';
  const normalized = String(value).trim();
  return normalized || '';
};

const resolveSucursalActivaId = (sucursalActiva, sucursal, user) => (
  toNullableString(
    sucursalActiva?.id
    || sucursalActiva?.sucursal_id
    || sucursal?.id
    || sucursal?.sucursal_id
    || user?.sucursal_activa_id
    || user?.sucursal_id
    || user?.sucursal?.id
  )
);

const resolveClienteProvinciaId = cliente => toNullableString(
  cliente?.provinciaId
  || cliente?.provincia_id
  || cliente?.domicilios?.find(item => item?.esDefault && item?.activo !== false)?.provinciaId
  || cliente?.domicilios?.find(item => item?.activo !== false)?.provinciaId
  || cliente?.provincia?.id
);

const resolveClienteLocalidadId = cliente => toNullableString(
  cliente?.localidadId
  || cliente?.localidad_id
  || cliente?.domicilios?.find(item => item?.esDefault && item?.activo !== false)?.localidadId
  || cliente?.domicilios?.find(item => item?.activo !== false)?.localidadId
  || cliente?.localidad?.id
);

const resolveClienteDireccion = cliente => toNullableString(
  cliente?.direccion
  || cliente?.domicilio
  || cliente?.domicilios?.find(item => item?.esDefault && item?.activo !== false)?.direccion
  || cliente?.domicilios?.find(item => item?.activo !== false)?.direccion
);

const resolveClienteTelefono = cliente => toNullableString(
  cliente?.telefono
  || cliente?.celular
  || cliente?.contactos?.find(item => item?.principal && item?.activo !== false)?.telefono
  || cliente?.contactos?.find(item => item?.activo !== false)?.telefono
  || cliente?.contacto?.telefono
);

const resolveClienteContacto = cliente => toNullableString(
  cliente?.contacto
  || cliente?.contacto_principal
  || cliente?.contactos?.find(item => item?.principal && item?.activo !== false)?.nombre
  || cliente?.contactos?.find(item => item?.activo !== false)?.nombre
);

const formatCurrency = value => {
  if (value == null || value === '') return '-';
  const amount = Number(value);
  if (Number.isNaN(amount)) return '-';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(amount);
};

const formatDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-AR');
};
const formatRetiroNumber = retiro => {
  const pv = retiro?.puntoVentaNumero;
  const nro = String(retiro?.nroRetiro || '').trim();
  return pv == null || pv === '' ? nro || '-' : `${String(pv).padStart(4, '0')}-${nro}`;
};

const normalizeRetiroData = data => {
  const base = initialForm();
  const normalized = { ...base, ...data };
  const remitoParts = splitRemitoCliente(normalized.remitoCliente);
  normalized.remitoClientePuntoVenta = remitoParts.puntoVenta;
  normalized.remitoClienteNumero = remitoParts.numero;
  normalized.remitoCliente = buildRemitoCliente(remitoParts.puntoVenta, remitoParts.numero);

  Object.keys(normalized).forEach(key => {
    if (normalized[key] == null) normalized[key] = '';
  });

  ['importe', 'bultos', 'kilosReales', 'mts3', 'kilosAforados', 'valorDeclarado'].forEach(
    key => {
      if (normalized[key] !== '') {
        normalized[key] = String(normalized[key]);
      }
    }
  );

  normalized.disponibilidadHoraria = sanitizeAvailabilityRanges(normalized.disponibilidadHoraria);

  return normalized;
};

const cx = (...classes) => classes.filter(Boolean).join(' ');
const isAbortError = error => error?.message === 'La solicitud fue cancelada.';
const buildListFilter = filters => Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
const HIGH_DECLARED_VALUE_THRESHOLD = 500000;
const SHORT_PHONE_LENGTH = 8;
const getDigits = value => String(value || '').replace(/\D/g, '');
const normalizeRemitoSegment = (value, maxLength) => getDigits(value).slice(0, maxLength);
const splitRemitoCliente = value => {
  const digits = normalizeRemitoSegment(value, 12);
  if (!digits) return { puntoVenta: '', numero: '' };
  if (digits.length <= 4) {
    return { puntoVenta: digits, numero: '' };
  }
  return {
    puntoVenta: digits.slice(0, 4),
    numero: digits.slice(4, 12),
  };
};
const buildRemitoCliente = (puntoVenta, numero) => {
  const normalizedPuntoVenta = normalizeRemitoSegment(puntoVenta, 4);
  const normalizedNumero = normalizeRemitoSegment(numero, 8);
  if (!normalizedPuntoVenta && !normalizedNumero) return '';
  return `${normalizedPuntoVenta.padStart(4, '0')}${normalizedNumero.padStart(8, '0')}`;
};
const getPhoneWarning = value => {
  const digits = getDigits(value);
  if (!digits.length || digits.length >= SHORT_PHONE_LENGTH) return '';
  return 'Revisá el teléfono: parece incompleto para gestión operativa.';
};
const getDeclaredValueWarning = value => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount < HIGH_DECLARED_VALUE_THRESHOLD) return '';
  return 'Valor declarado alto. Confirmalo antes de emitir el retiro.';
};
const getWeightWarning = formData => {
  const kilosReales = Number(formData.kilosReales);
  const kilosAforados = Number(formData.kilosAforados);
  if (!Number.isFinite(kilosReales) || !Number.isFinite(kilosAforados)) return '';
  if (kilosAforados >= kilosReales) return '';
  return 'Los kilos aforados quedaron por debajo de los kilos reales.';
};
const getAvailabilitySummary = ranges => {
  const normalized = sanitizeAvailabilityRanges(ranges).filter(range => range.desde && range.hasta);
  if (!normalized.length) return 'Sin ventana cargada';
  return normalized.map(range => `${range.desde}–${range.hasta}`).join(' · ');
};
const getRetiroErrorMessage = error => error?.detail || error?.message || 'No pudimos guardar el retiro.';
const buildOperationalWarnings = formData => {
  const warnings = [];
  const remitentePhoneWarning = getPhoneWarning(formData.remitenteTelefono);
  const destinatarioPhoneWarning = getPhoneWarning(formData.destinatarioTelefono);
  const declaredValueWarning = getDeclaredValueWarning(formData.valorDeclarado);
  const weightWarning = getWeightWarning(formData);

  if (remitentePhoneWarning) warnings.push(remitentePhoneWarning);
  if (destinatarioPhoneWarning) warnings.push(destinatarioPhoneWarning);
  if (declaredValueWarning) warnings.push(declaredValueWarning);
  if (weightWarning) warnings.push(weightWarning);

  return warnings;
};

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timeoutId);
  }, [value, delay]);

  return debounced;
}

function sanitizeRetiroPayload(formData) {
  const payload = Object.entries(formData).reduce((acc, [key, rawValue]) => {
    if (!RETIRO_INPUT_FIELD_NAMES.has(key)) {
      return acc;
    }

    if (key === 'remitoClientePuntoVenta' || key === 'remitoClienteNumero') {
      return acc;
    }

    if (NUMERIC_FIELDS.includes(key)) {
      acc[key] = rawValue === '' ? null : Number(rawValue);
      return acc;
    }

    if (ID_FIELDS.includes(key)) {
      acc[key] = rawValue === '' ? null : rawValue;
      return acc;
    }

    if (DATETIME_FIELDS.includes(key)) {
      acc[key] = rawValue || null;
      return acc;
    }

    if (key === 'disponibilidadHoraria') {
      const ranges = sanitizeAvailabilityRanges(rawValue)
        .filter(range => range.desde && range.hasta);
      acc[key] = ranges.length ? ranges : null;
      return acc;
    }

    acc[key] = typeof rawValue === 'string' ? rawValue.trim() || null : rawValue;
    return acc;
  }, {});

  payload.remitoCliente = buildRemitoCliente(formData.remitoClientePuntoVenta, formData.remitoClienteNumero) || formData.remitoCliente || '';
  return payload;
}
function validateRetiroForm(formData) {
  const errors = {};
  if (!formData.remitenteClienteId) errors.remitenteClienteId = 'Seleccioná un remitente desde el buscador.';
  if (!formData.destinatarioClienteId) errors.destinatarioClienteId = 'Seleccioná un destinatario desde el buscador.';

  NUMERIC_FIELDS.forEach(field => {
    if (formData[field] === '') return;
    const value = Number(formData[field]);
    if (Number.isNaN(value)) {
      errors[field] = 'Ingresá un número válido.';
      return;
    }
    if (value < 0) {
      errors[field] = 'El valor no puede ser negativo.';
    }
  });

  const invalidIndex = (formData.disponibilidadHoraria || []).findIndex(range => {
    const desde = range?.desde?.trim();
    const hasta = range?.hasta?.trim();
    if (!desde && !hasta) return false;
    if (!desde || !hasta) return true;
    return desde >= hasta;
  });

  if (invalidIndex >= 0) {
    errors.disponibilidadHoraria = 'Revisá la ventana de retiro: completá desde/hasta y mantené un orden válido.';
  }

  return errors;
}
export default function RetirosPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { sucursalActiva, sucursal, user } = useAuth();
  const [activeTab, setActiveTab] = useState('listado');
  const [filters, setFilters] = useState({ q: '', nro: '', estado: '', sucursalId: '', fechaDesde: '', fechaHasta: '' });
  const debouncedFilters = useDebouncedValue(filters, FILTER_DEBOUNCE_MS);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [retiros, setRetiros] = useState([]);
  const [totalRetiros, setTotalRetiros] = useState(0);
  const [retiroSucursales, setRetiroSucursales] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState('');
  const [selectedRetiroId, setSelectedRetiroId] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [formErrors, setFormErrors] = useState({});
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [modalTarget, setModalTarget] = useState(null);
  const [remitenteCustomer, setRemitenteCustomer] = useState(null);
  const [destinatarioCustomer, setDestinatarioCustomer] = useState(null);
  const detailRequestRef = useRef(null);
  const formContainerRef = useRef(null);
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === 'undefined' ? 1440 : window.innerWidth,
    height: typeof window === 'undefined' ? 900 : window.innerHeight,
  }));
  const sucursalId = useMemo(
    () => resolveSucursalActivaId(sucursalActiva, sucursal, user),
    [sucursalActiva, sucursal, user],
  );

  useEffect(() => {
    if (!sucursalId) return;
    setFormData(prev => (prev.sucursalId ? prev : { ...prev, sucursalId }));
  }, [sucursalId]);

  useEffect(() => {
    const handleResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const resetForm = useCallback(() => {
    detailRequestRef.current?.abort();
    setFormData(initialForm());
    setFormErrors({});
    setSaveError('');
    setSelectedRetiroId(null);
    setRemitenteCustomer(null);
    setDestinatarioCustomer(null);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setLoadingList(true);
      setListError('');
      try {
        const listFilter = buildListFilter(debouncedFilters);
        const [data, total] = await Promise.all([
          fetchRetiros({ filter: listFilter, limit, offset }, { signal: controller.signal }),
          fetchRetirosCount(listFilter, { signal: controller.signal }),
        ]);
        setRetiros(data);
        setTotalRetiros(total);
      } catch (error) {
        if (isAbortError(error)) return;
        console.error('Error cargando retiros', error);
        setRetiros([]);
        setTotalRetiros(0);
        setListError(error.message || 'No pudimos cargar el listado de retiros.');
        toast.error('No pudimos cargar el listado de retiros.');
      } finally {
        if (!controller.signal.aborted) {
          setLoadingList(false);
        }
      }
    };

    load();
    return () => controller.abort();
  }, [debouncedFilters, limit, offset, toast]);

  useEffect(() => {
    const controller = new AbortController();
    fetchRetiroSucursales({ signal: controller.signal })
      .then(setRetiroSucursales)
      .catch(error => {
        if (!isAbortError(error)) console.error('Error cargando sucursales de retiros', error);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (formData.remitenteClienteId && String(remitenteCustomer?.id || '') !== String(formData.remitenteClienteId)) {
      const controller = new AbortController();
      getClienteById(formData.remitenteClienteId, { signal: controller.signal })
        .then(setRemitenteCustomer)
        .catch(() => null);
      return () => controller.abort();
    }
  }, [formData.remitenteClienteId, remitenteCustomer?.id]);

  useEffect(() => {
    if (formData.destinatarioClienteId && String(destinatarioCustomer?.id || '') !== String(formData.destinatarioClienteId)) {
      const controller = new AbortController();
      getClienteById(formData.destinatarioClienteId, { signal: controller.signal })
        .then(setDestinatarioCustomer)
        .catch(() => null);
      return () => controller.abort();
    }
  }, [formData.destinatarioClienteId, destinatarioCustomer?.id]);

  const hasMore = useMemo(() => offset + retiros.length < totalRetiros, [offset, retiros.length, totalRetiros]);
  const isLowHeight = viewport.height <= 700;
  const isCompactDesktop = viewport.width >= 1280 && viewport.width <= 1366;
  const isStackedHeavySections = viewport.width <= 1200;
  const isSingleColumn = viewport.width <= 1024;

  const cardPadding = isLowHeight ? 'p-2.5' : isCompactDesktop ? 'p-3' : 'p-3.5';
  const sectionStack = isLowHeight ? 'space-y-2' : 'space-y-2.5';
  const formBottomPadding = isLowHeight ? 'pb-20' : 'pb-24';
  const labelCompact = true;
  const fieldClassName =
    'h-9 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--bg))] px-2.5 py-1 text-[12px] text-[rgb(var(--fg))] shadow-sm outline-none transition focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]';
  const areaClassName =
    'min-h-[52px] w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--bg))] px-2.5 py-1.5 text-[12px] text-[rgb(var(--fg))] shadow-sm outline-none transition focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]';
  const selectClassName =
    'h-9 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--bg))] px-2.5 py-1 text-[12px] text-[rgb(var(--fg))] shadow-sm outline-none transition focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]';
  const operationalWarnings = useMemo(() => buildOperationalWarnings(formData), [formData]);
  const summaryItems = useMemo(() => ([
    { label: 'Origen', value: formData.remitenteRazonSocial || 'Sin remitente' },
    { label: 'Destino', value: formData.destinatarioRazonSocial || 'Sin destinatario' },
    { label: 'Bultos', value: formData.bultos || '0' },
    { label: 'Kg reales', value: formData.kilosReales || '0' },
    { label: 'Kg aforados', value: formData.kilosAforados || '0' },
    { label: 'Valor declarado', value: formData.valorDeclarado ? formatCurrency(formData.valorDeclarado) : '-' },
    { label: 'Pago', value: formData.formaPago || 'Sin definir' },
    { label: 'Ventana', value: getAvailabilitySummary(formData.disponibilidadHoraria) },
  ]), [formData]);

  const updateField = (name, value) => {
    setSaveError('');
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };


  const updateRemitoClientePart = (part, value) => {
    setSaveError('');
    setFormData(prev => {
      const nextPuntoVenta = part === 'puntoVenta'
        ? normalizeRemitoSegment(value, 4)
        : prev.remitoClientePuntoVenta;
      const nextNumero = part === 'numero'
        ? normalizeRemitoSegment(value, 8)
        : prev.remitoClienteNumero;

      return {
        ...prev,
        remitoClientePuntoVenta: nextPuntoVenta,
        remitoClienteNumero: nextNumero,
        remitoCliente: buildRemitoCliente(nextPuntoVenta, nextNumero),
      };
    });
  };

  const padRemitoClienteParts = () => {
    setFormData(prev => {
      const puntoVenta = prev.remitoClientePuntoVenta
        ? normalizeRemitoSegment(prev.remitoClientePuntoVenta, 4).padStart(4, '0')
        : '';
      const numero = prev.remitoClienteNumero
        ? normalizeRemitoSegment(prev.remitoClienteNumero, 8).padStart(8, '0')
        : '';

      return {
        ...prev,
        remitoClientePuntoVenta: puntoVenta,
        remitoClienteNumero: numero,
        remitoCliente: buildRemitoCliente(puntoVenta, numero),
      };
    });
  };
  const updateAvailabilityRange = (index, key, value) => {
    setSaveError('');
    setFormData(prev => ({
      ...prev,
      disponibilidadHoraria: (prev.disponibilidadHoraria || []).map((range, rangeIndex) =>
        rangeIndex === index ? { ...range, [key]: value } : range
      ),
    }));
    setFormErrors(prev => {
      if (!prev.disponibilidadHoraria) return prev;
      const next = { ...prev };
      delete next.disponibilidadHoraria;
      return next;
    });
  };

  const addAvailabilityRange = () => {
    setSaveError('');
    setFormData(prev => ({
      ...prev,
      disponibilidadHoraria: [...(prev.disponibilidadHoraria || []), { ...EMPTY_TIME_RANGE }],
    }));
  };

  const removeAvailabilityRange = index => {
    setSaveError('');
    setFormData(prev => {
      const nextRanges = (prev.disponibilidadHoraria || []).filter((_, rangeIndex) => rangeIndex !== index);
      return {
        ...prev,
        disponibilidadHoraria: nextRanges.length ? nextRanges : [{ ...EMPTY_TIME_RANGE }],
      };
    });
  };

  const focusFirstUsefulField = useCallback(() => {
    window.requestAnimationFrame(() => {
      formContainerRef.current
        ?.querySelector('input[name="remitenteClienteSearch"], input[name="destinatarioClienteSearch"], input, select, textarea')
        ?.focus();
    });
  }, []);

  useEffect(() => {
    if (activeTab !== 'form' || isLoadingDetail) return;
    focusFirstUsefulField();
  }, [activeTab, isLoadingDetail, selectedRetiroId, focusFirstUsefulField]);

  const handleFormKeyDown = useCallback(event => {
    if (event.defaultPrevented || event.key !== 'Enter' || event.shiftKey) return;
    const tagName = event.target?.tagName;
    if (tagName === 'TEXTAREA') return;

    const fields = Array.from(
      formContainerRef.current?.querySelectorAll('input, select, textarea, button[type="button"]') || []
    ).filter(element => {
      if (element.disabled) return false;
      if (element.type === 'hidden') return false;
      if (element.getAttribute('aria-hidden') === 'true') return false;
      return element.offsetParent !== null;
    });

    const currentIndex = fields.indexOf(event.target);
    if (currentIndex === -1) return;

    event.preventDefault();
    fields[currentIndex + 1]?.focus();
  }, []);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setOffset(0);
  };

  const renderField = (name, value, onChange, props = {}) => (
    <input
      name={name}
      value={value}
      onChange={onChange}
      onKeyDown={props.onKeyDown || handleFormKeyDown}
      className={fieldClassName}
      aria-invalid={Boolean(formErrors[name])}
      {...props}
    />
  );

  const renderFieldError = name =>
    formErrors[name] ? <p className="mt-1 text-[11px] font-medium text-rose-600">{formErrors[name]}</p> : null;

  const renderInlineWarning = message =>
    message ? <p className="mt-1 text-[11px] font-medium text-amber-600">{message}</p> : null;

  const applyCustomer = (target, customer) => {
    if (!customer) return;
    setSaveError('');
    if (target === 'remitente') {
      setRemitenteCustomer(customer);
    } else {
      setDestinatarioCustomer(customer);
    }

    setFormData(prev => ({
      ...prev,
      sucursalId: prev.sucursalId || sucursalId,
      [`${target}ClienteId`]: customer.id || '',
      [`${target}RazonSocial`]: customer.razonSocial || '',
      [`${target}Domicilio`]: resolveClienteDireccion(customer),
      [`${target}Cp`]: customer.codigoPostal || '',
      [`${target}ProvinciaId`]: resolveClienteProvinciaId(customer),
      [`${target}LocalidadId`]: resolveClienteLocalidadId(customer),
      [`${target}Telefono`]: resolveClienteTelefono(customer),
      [`${target}Contacto`]: resolveClienteContacto(customer),
    }));
  };

  const handleSelectRetiro = async retiroId => {
    detailRequestRef.current?.abort();
    const controller = new AbortController();
    detailRequestRef.current = controller;
    setSelectedRetiroId(retiroId);
    setActiveTab('form');
    setIsLoadingDetail(true);
    try {
      const data = await fetchRetiro(retiroId, { signal: controller.signal });
      setFormData(normalizeRetiroData(data || {}));
      setRemitenteCustomer(null);
      setDestinatarioCustomer(null);
    } catch (error) {
      if (!isAbortError(error)) {
        console.error('Error cargando retiro', error);
        toast.error('No pudimos cargar el retiro seleccionado.');
      }
    } finally {
      if (detailRequestRef.current === controller) {
        detailRequestRef.current = null;
      }
      setIsLoadingDetail(false);
    }
  };

  const handleSubmit = async (mode = 'back-to-list') => {
    if (isSaving) return;

    const payload = sanitizeRetiroPayload({
      ...formData,
      sucursalId: formData.sucursalId || sucursalId || null,
    });

    const nextErrors = validateRetiroForm(payload);
    if (!payload.sucursalId) {
      nextErrors.sucursalId = 'No pudimos identificar la sucursal activa para emitir el retiro.';
    }

    setFormErrors(nextErrors);
    setSaveError('');

    const firstInvalidField = Object.keys(nextErrors)[0];
    if (firstInvalidField) {
      const focusTarget =
        firstInvalidField === 'remitenteClienteId'
          ? 'remitenteClienteSearch'
          : firstInvalidField === 'destinatarioClienteId'
            ? 'destinatarioClienteSearch'
            : firstInvalidField;
      document.querySelector(`[name="${focusTarget}"]`)?.focus();
      toast.warning({
        sinaState: 'warning',
        title: 'Faltan datos para guardar el retiro',
        message: 'Revisá los campos obligatorios antes de continuar.',
      });
      return;
    }

    const weightWarning = getWeightWarning(formData);
    if (weightWarning) {
      toast.warning({
        sinaState: 'warning',
        title: 'Revisá el aforo antes de continuar',
        message: weightWarning,
      });
    }

    setIsSaving(true);
    try {
      const savedRetiro = selectedRetiroId
        ? await updateRetiro(selectedRetiroId, payload)
        : await createRetiro(payload);

      toast.success({
        sinaState: 'success',
        title: selectedRetiroId ? 'Retiro actualizado' : 'Retiro creado',
        message: selectedRetiroId ? 'Los cambios fueron guardados correctamente.' : 'El retiro quedó registrado y listo para gestionar.',
      });
      setOffset(0);
      setRetiros(await fetchRetiros({ filter: buildListFilter(filters), limit, offset: 0 }));
      setListError('');

      if (mode === 'create-another' && !selectedRetiroId) {
        resetForm();
        setActiveTab('form');
        toast.info({
          sinaState: 'help',
          title: 'Retiro listo',
          message: `El retiro ${savedRetiro?.nroRetiro || 'creado'} fue guardado. Podés cargar otro.`,
        });
        return;
      }

      resetForm();
      setActiveTab('listado');
    } catch (error) {
      const message = getRetiroErrorMessage(error);
      console.error('Error guardando retiro', error);
      setSaveError(message);
      toast.error({
        sinaState: 'error',
        title: 'No pudimos guardar el retiro',
        message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const refreshList = useCallback(async () => {
    setRetiros(await fetchRetiros({ filter: buildListFilter(filters), limit, offset }));
  }, [filters, limit, offset]);

  const avanzarEstado = async (retiro, estado) => {
    try {
      await cambiarEstadoRetiro(retiro.id, estado);
      toast.success({
        sinaState: 'success',
        title: estado === 'EN_RECOLECCION' ? 'Retiro enviado a recolección' : 'Retiro confirmado como retirado',
        message: 'La operación fue actualizada correctamente.',
      });
      await refreshList();
    } catch (error) {
      toast.error({
        sinaState: 'error',
        title: 'No pudimos actualizar el retiro',
        message: getRetiroErrorMessage(error),
      });
    }
  };

  const emitirGuiaDesdeRetiro = retiro => {
    navigate('/guias/emitir', { state: { retiroOrigen: retiro } });
  };

  const renderPartyCard = (target, title, selectedCustomer) => (
    <section className={cx('rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] shadow-[0_8px_22px_rgba(var(--shadow),0.08)]', cardPadding)}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <span className="inline-flex rounded-full border border-[rgba(var(--brand),0.18)] bg-[rgba(var(--brand),0.08)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--brand))]">
            {target === 'remitente' ? 'Origen / Remitente' : 'Destino / Destinatario'}
          </span>
          <h2 className="mt-1 text-[13px] font-semibold text-[rgb(var(--fg))]">{title}</h2>
        </div>
        <span className="text-[11px] font-medium text-[rgb(var(--muted))]">
          {target === 'remitente' ? 'Punto de retiro' : 'Punto de entrega'}
        </span>
      </div>

      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-12">
          <CustomerSelect
            label={`Cliente ${title.toLowerCase()}`}
            value={formData[`${target}ClienteId`]}
            selectedCustomer={selectedCustomer}
            onSelect={customer => applyCustomer(target, customer)}
            onOpenModal={() => setModalTarget(target)}
            inputName={`${target}ClienteSearch`}
            autoFocus={target === 'remitente'}
            onInputKeyDown={handleFormKeyDown}
            description=""
            density="compact"
          />
          {renderFieldError(`${target}ClienteId`)}
        </div>

        <Field label="Domicilio" compact={labelCompact} className="col-span-12">
          {renderField(`${target}Domicilio`, formData[`${target}Domicilio`], event =>
            updateField(`${target}Domicilio`, event.target.value)
          )}
        </Field>

        <Field label="Codigo postal" compact={labelCompact} className="col-span-12 sm:col-span-3">
          {renderField(`${target}Cp`, formData[`${target}Cp`], event =>
            updateField(`${target}Cp`, event.target.value)
          )}
        </Field>

        <div className="col-span-12 sm:col-span-4">
          <ProvinciaSelect
            label="Provincia"
            value={formData[`${target}ProvinciaId`]}
            onChange={value =>
              setFormData(prev => ({
                ...prev,
                [`${target}ProvinciaId`]: value,
                [`${target}LocalidadId`]: '',
              }))
            }
          />
        </div>

        <div className="col-span-12 sm:col-span-5">
          <LocalidadSelect
            label="Localidad"
            provinciaId={formData[`${target}ProvinciaId`]}
            value={formData[`${target}LocalidadId`]}
            onChange={value => updateField(`${target}LocalidadId`, value)}
            disabled={!formData[`${target}ProvinciaId`]}
          />
        </div>

        <Field label="Teléfono" compact={labelCompact} className="col-span-12 sm:col-span-6">
          {renderField(`${target}Telefono`, formData[`${target}Telefono`], event =>
            updateField(`${target}Telefono`, event.target.value)
          )}
          {renderInlineWarning(getPhoneWarning(formData[`${target}Telefono`]))}
        </Field>

        <Field label="Contacto" compact={labelCompact} className="col-span-12 sm:col-span-6">
          {renderField(`${target}Contacto`, formData[`${target}Contacto`], event =>
            updateField(`${target}Contacto`, event.target.value)
          )}
        </Field>
      </div>
    </section>
  );

  return (
    <div className="mx-auto w-full max-w-[1400px] overflow-x-hidden px-4 pb-8 pt-1 sm:px-4 lg:px-5">
      <PageHeader
        title="Retiros y Recolecciones"
        subtitle="Listado, alta y edición operativa en una vista compacta y estable para trabajo diario."
        actions={
          <>
            <div className="flex flex-wrap items-center gap-1 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.94)] p-1 shadow-[0_6px_18px_rgba(var(--shadow),0.06)]">
              {[
                { id: 'listado', label: 'Listado' },
                { id: 'form', label: selectedRetiroId ? 'Editar retiro' : 'Nuevo retiro' },
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={cx(
                    'rounded-xl px-3 py-1.5 text-[13px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]',
                    activeTab === tab.id
                      ? 'bg-[rgba(var(--brand),0.14)] text-[rgb(var(--fg))]'
                      : 'text-[rgb(var(--muted))] hover:bg-[rgba(var(--muted),0.08)] hover:text-[rgb(var(--fg))]'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                resetForm();
                setActiveTab('form');
              }}
              className="inline-flex h-9 items-center rounded-xl bg-[rgb(var(--brand))] px-4 text-sm font-semibold text-white transition hover:bg-[rgb(var(--brand-700))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              Nuevo retiro
            </button>
          </>
        }
      />

      {activeTab === 'listado' ? (
        <div className="mt-2 space-y-2">
          <div className={cx('rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] shadow-[0_10px_28px_rgba(var(--shadow),0.08)]', isLowHeight ? 'p-3' : 'p-3.5')}>
            <div className="grid grid-cols-12 gap-3">
              <Field label="Buscar" compact className="col-span-12 lg:col-span-4">
                {renderField('q', filters.q, event => handleFilterChange('q', event.target.value), {
                  placeholder: 'Nro, remitente, destinatario o descripción',
                })}
              </Field>
              <Field label="Número" compact className="col-span-12 sm:col-span-6 lg:col-span-2">
                {renderField('nro', filters.nro, event => handleFilterChange('nro', event.target.value))}
              </Field>
              <Field label="Estado" compact className="col-span-12 sm:col-span-6 lg:col-span-2">
                <select
                  value={filters.estado}
                  onChange={event => handleFilterChange('estado', event.target.value)}
                  className={selectClassName}
                >
                  <option value="">Todos</option>
                  {ESTADOS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Sucursal emisora" compact className="col-span-12 sm:col-span-6 lg:col-span-2">
                <select
                  value={filters.sucursalId}
                  onChange={event => handleFilterChange('sucursalId', event.target.value)}
                  className={selectClassName}
                >
                  <option value="">Todas</option>
                  {retiroSucursales.map(sucursal => (
                    <option key={sucursal.id} value={sucursal.id}>
                      {sucursal.nombre}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Fecha desde" compact className="col-span-12 sm:col-span-6 lg:col-span-1">
                {renderField('fechaDesde', filters.fechaDesde, event => handleFilterChange('fechaDesde', event.target.value), {
                  type: 'date',
                })}
              </Field>
              <Field label="Fecha hasta" compact className="col-span-12 sm:col-span-6 lg:col-span-1">
                {renderField('fechaHasta', filters.fechaHasta, event => handleFilterChange('fechaHasta', event.target.value), {
                  type: 'date',
                })}
              </Field>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] shadow-[0_10px_28px_rgba(var(--shadow),0.08)]">
            {listError && !loadingList ? (
              <div className="border-b border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.55)] px-4 py-2 text-sm text-[rgb(var(--muted))]">
                {listError}
              </div>
            ) : null}
            <div className="overflow-x-auto">
              {!loadingList && retiros.length === 0 ? (
                <div className="p-5">
                  <EmptyState
                    title="No hay retiros para mostrar"
                    description="Probá ajustando los filtros o cargá un nuevo retiro desde el botón superior."
                  />
                </div>
              ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-[rgb(var(--subpanel))] text-[rgb(var(--muted))]">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold">Fecha</th>
                    <th className="px-4 py-2 text-left font-semibold">Nro</th>
                    {!isStackedHeavySections ? <th className="px-4 py-2 text-left font-semibold">Sucursal emisora</th> : null}
                    {!isStackedHeavySections ? <th className="px-4 py-2 text-left font-semibold">Sucursal de recolección</th> : null}
                    {!isSingleColumn ? <th className="px-4 py-2 text-left font-semibold">Remitente</th> : null}
                    {!isStackedHeavySections ? <th className="px-4 py-2 text-left font-semibold">Destinatario</th> : null}
                    {!isCompactDesktop && !isSingleColumn ? <th className="px-4 py-2 text-left font-semibold">Pago</th> : null}
                    {!isCompactDesktop && !isSingleColumn ? <th className="px-4 py-2 text-right font-semibold">Importe</th> : null}
                    <th className="px-4 py-2 text-left font-semibold">Estado</th>
                    <th className="px-4 py-2 text-right font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgb(var(--border))]">
                  {loadingList ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-6 text-[rgb(var(--muted))]">
                        Cargando retiros...
                      </td>
                    </tr>
                  ) : null}

                  {!loadingList &&
                    retiros.map(retiro => (
                      <tr key={retiro.id}>
                        <td className="px-4 py-2 text-[rgb(var(--fg))]">{formatDate(retiro.fechaEmision)}</td>
                        <td className="px-4 py-2 font-medium text-[rgb(var(--fg))]">{formatRetiroNumber(retiro)}</td>
                        {!isStackedHeavySections ? <td className="px-4 py-2 text-[rgb(var(--fg))]">{retiro.sucursalEmisoraNombre || '-'}</td> : null}
                        {!isStackedHeavySections ? <td className="px-4 py-2 text-[rgb(var(--fg))]">{retiro.sucursalRetiroLabel || '-'}</td> : null}
                        {!isSingleColumn ? <td className="px-4 py-2 text-[rgb(var(--fg))]">{retiro.remitenteRazonSocial || '-'}</td> : null}
                        {!isStackedHeavySections ? <td className="px-4 py-2 text-[rgb(var(--fg))]">{retiro.destinatarioRazonSocial || '-'}</td> : null}
                        {!isCompactDesktop && !isSingleColumn ? <td className="px-4 py-2 text-[rgb(var(--fg))]">{retiro.formaPago || '-'}</td> : null}
                        {!isCompactDesktop && !isSingleColumn ? <td className="px-4 py-2 text-right text-[rgb(var(--fg))]">{formatCurrency(retiro.importe)}</td> : null}
                        <td className="px-4 py-2 text-[rgb(var(--fg))]">{ESTADOS.find(option => option.value === retiro.estado)?.label || retiro.estado}</td>
                        <td className="px-4 py-2 text-right">
                          {retiro.estado === 'TOMADO' ? <button
                            type="button"
                            onClick={() => avanzarEstado(retiro, 'EN_RECOLECCION')}
                            className="mr-2 rounded-lg bg-[rgb(var(--brand))] px-3 py-1 text-xs font-semibold text-white transition hover:opacity-90"
                          >Enviar a recolección</button> : null}
                          {retiro.estado === 'EN_RECOLECCION' ? <button
                            type="button"
                            onClick={() => avanzarEstado(retiro, 'RETIRADO')}
                            className="mr-2 rounded-lg bg-[rgb(var(--brand))] px-3 py-1 text-xs font-semibold text-white transition hover:opacity-90"
                          >Confirmar retiro</button> : null}
                          {retiro.estado === 'RETIRADO' && !retiro.guiaId ? <button
                            type="button"
                            onClick={() => emitirGuiaDesdeRetiro(retiro)}
                            className="mr-2 rounded-lg bg-[rgb(var(--brand))] px-3 py-1 text-xs font-semibold text-white transition hover:opacity-90"
                          >Emitir guía</button> : null}
                          {retiro.guiaId ? <button type="button" onClick={() => navigate(`/guias/${retiro.guiaId}`)} className="mr-2 rounded-lg border border-[rgb(var(--border))] px-3 py-1 text-xs font-semibold text-[rgb(var(--fg))]">Ver guía</button> : null}
                          <button
                            type="button"
                            onClick={() => handleSelectRetiro(retiro.id)}
                            className="rounded-lg border border-[rgb(var(--border))] px-3 py-1 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                          >
                            Editar
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[rgb(var(--border))] px-4 py-2">
              <p className="text-xs text-[rgb(var(--muted))]">
                {totalRetiros} {totalRetiros === 1 ? 'retiro listado' : 'retiros listados'} · Página {Math.floor(offset / limit) + 1}
              </p>
              <div className="flex items-center gap-2">
                <select
                  value={limit}
                  onChange={event => {
                    setLimit(Number(event.target.value));
                    setOffset(0);
                  }}
                  className="rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--bg))] px-3 py-1.5 text-xs text-[rgb(var(--fg))]"
                >
                  {[10, 20, 50].map(size => (
                    <option key={size} value={size}>
                      {size} filas
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  className="rounded-lg border border-[rgb(var(--border))] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.2)]"
                  disabled={offset === 0}
                >
                  Anterior
                </button>
                <button
                  type="button"
                  onClick={() => setOffset(offset + limit)}
                  className="rounded-lg border border-[rgb(var(--border))] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.2)]"
                  disabled={!hasMore}
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div ref={formContainerRef} onKeyDown={handleFormKeyDown} className={cx('mt-3', sectionStack, formBottomPadding)}>
          {saveError ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <p className="font-semibold">No pudimos guardar el retiro</p>
              <p className="mt-1">{saveError}</p>
            </div>
          ) : null}

          <div className="grid gap-3 md:grid-cols-12">
            <div className="md:col-span-6">
              {renderPartyCard('remitente', 'Remitente', remitenteCustomer)}
            </div>
            <div className="md:col-span-6">
              {renderPartyCard('destinatario', 'Destinatario', destinatarioCustomer)}
            </div>
          </div>

          <section className={cx('rounded-2xl border border-[rgba(var(--brand),0.15)] bg-[linear-gradient(180deg,rgba(var(--panel),0.98),rgba(var(--subpanel),0.72))] shadow-[0_8px_22px_rgba(var(--shadow),0.08)]', cardPadding)}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--muted))]">Resumen operativo</p>
                <h2 className="mt-1 text-[14px] font-semibold text-[rgb(var(--fg))]">Chequeo rápido antes de guardar</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {summaryItems.map(item => (
                  <div key={item.label} className="rounded-full border border-[rgba(var(--border),0.9)] bg-[rgba(var(--panel),0.84)] px-3 py-1 text-[11px] text-[rgb(var(--fg))]">
                    <span className="font-semibold text-[rgb(var(--muted))]">{item.label}:</span> {item.value}
                  </div>
                ))}
              </div>
            </div>
            {operationalWarnings.length ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {operationalWarnings.map(warning => (
                  <span key={warning} className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-700">
                    {warning}
                  </span>
                ))}
              </div>
            ) : null}
          </section>

          <section className={cx('rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] shadow-[0_8px_22px_rgba(var(--shadow),0.08)]', cardPadding)}>
            <div className="mb-1.5">
              <h2 className="text-[13px] font-semibold text-[rgb(var(--fg))]">Datos del envío</h2>
            </div>
            <div className="grid grid-cols-12 gap-2">
              <Field label="Remito cliente" compact={labelCompact} className="col-span-12 md:col-span-6 lg:col-span-5">
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-12 sm:col-span-4">
                    <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.18em] text-[rgb(var(--muted))]">Pto venta</label>
                    {renderField('remitoClientePuntoVenta', formData.remitoClientePuntoVenta, event => updateRemitoClientePart('puntoVenta', event.target.value), {
                      inputMode: 'numeric',
                      maxLength: 4,
                      placeholder: '0001',
                      onBlur: padRemitoClienteParts,
                    })}
                  </div>
                  <div className="col-span-12 sm:col-span-8">
                    <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.18em] text-[rgb(var(--muted))]">Número</label>
                    {renderField('remitoClienteNumero', formData.remitoClienteNumero, event => updateRemitoClientePart('numero', event.target.value), {
                      inputMode: 'numeric',
                      maxLength: 8,
                      placeholder: '00000001',
                      onBlur: padRemitoClienteParts,
                    })}
                  </div>
                </div>
              </Field>
              <Field label="Bultos" compact={labelCompact} className="col-span-6 md:col-span-2 lg:col-span-1">
                {renderField('bultos', formData.bultos, event => updateField('bultos', event.target.value), { type: 'number' })}
                {renderFieldError('bultos')}
              </Field>
              <Field label="Kilos reales" compact={labelCompact} className="col-span-6 md:col-span-3 lg:col-span-2">
                {renderField('kilosReales', formData.kilosReales, event => updateField('kilosReales', event.target.value), {
                  type: 'number',
                  step: '0.001',
                })}
                {renderFieldError('kilosReales')}
              </Field>
              <Field label="MTS3" compact={labelCompact} className="col-span-6 md:col-span-2">
                {renderField('mts3', formData.mts3, event => updateField('mts3', event.target.value), {
                  type: 'number',
                  step: '0.001',
                })}
                {renderFieldError('mts3')}
              </Field>
              <Field label="Kilos aforados" compact={labelCompact} className="col-span-6 md:col-span-2 lg:col-span-2">
                {renderField('kilosAforados', formData.kilosAforados, event => updateField('kilosAforados', event.target.value), {
                  type: 'number',
                  step: '0.001',
                })}
                {renderFieldError('kilosAforados')}
                {renderInlineWarning(getWeightWarning(formData))}
              </Field>
              <Field label="Valor declarado" compact={labelCompact} className="col-span-6 md:col-span-2 lg:col-span-2">
                {renderField('valorDeclarado', formData.valorDeclarado, event => updateField('valorDeclarado', event.target.value), {
                  type: 'number',
                  step: '0.01',
                })}
                {renderFieldError('valorDeclarado')}
                {renderInlineWarning(getDeclaredValueWarning(formData.valorDeclarado))}
              </Field>
              <Field label="Tipo de pago" compact={labelCompact} className="col-span-12 md:col-span-4 lg:col-span-3">
                <select
                  name="formaPago"
                  value={formData.formaPago}
                  onChange={event => updateField('formaPago', event.target.value)}
                  onKeyDown={handleFormKeyDown}
                  className={selectClassName}
                >
                  <option value="">Seleccionar</option>
                  {FORMA_PAGO.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Sucursal de recolección" compact={labelCompact} className="col-span-12 md:col-span-4 lg:col-span-3">
                <select
                  name="sucursalRetiroLabel"
                  value={formData.sucursalRetiroLabel}
                  onChange={event => updateField('sucursalRetiroLabel', event.target.value)}
                  onKeyDown={handleFormKeyDown}
                  className={selectClassName}
                >
                  <option value="">Seleccionar</option>
                  {retiroSucursales.map(sucursal => (
                    <option key={sucursal.id} value={sucursal.nombre}>
                      {sucursal.nombre}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Descripción de mercadería" compact={labelCompact} className="col-span-12 md:col-span-8 lg:col-span-5">
                {renderField('descripcion', formData.descripcion, event => updateField('descripcion', event.target.value))}
              </Field>
              <Field label="Observaciones operativas" compact={labelCompact} className="col-span-12 md:col-span-8 lg:col-span-5">
                <textarea
                  name="observacion"
                  value={formData.observacion}
                  onChange={event => updateField('observacion', event.target.value)}
                  onKeyDown={handleFormKeyDown}
                  rows={1}
                  className={areaClassName}
                />
              </Field>

              <div className="col-span-12 md:col-span-4 lg:col-span-4">
                <div className="rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.45)] p-2">
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--muted))]">
                      Ventana de retiro
                    </p>
                    <button
                      type="button"
                      onClick={addAvailabilityRange}
                      className="rounded-lg border border-[rgb(var(--border))] px-2.5 py-1 text-[11px] font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.12)]"
                    >
                      Agregar rango
                    </button>
                  </div>

                  <div className="space-y-1.5">
                    {(formData.disponibilidadHoraria || []).map((range, index) => (
                      <div key={`disponibilidad-${index}`} className="grid grid-cols-12 items-end gap-1.5">
                        <Field label="Desde" compact className="col-span-5 md:col-span-4">
                          {renderField(`disponibilidadDesde${index}`, range.desde || '', event =>
                            updateAvailabilityRange(index, 'desde', event.target.value), { type: 'time' })}
                        </Field>
                        <Field label="Hasta" compact className="col-span-5 md:col-span-4">
                          {renderField(`disponibilidadHasta${index}`, range.hasta || '', event =>
                            updateAvailabilityRange(index, 'hasta', event.target.value), { type: 'time' })}
                        </Field>
                        <div className="col-span-2 md:col-span-4 flex items-end">
                          <button
                            type="button"
                            onClick={() => removeAvailabilityRange(index)}
                            className="h-9 w-full rounded-lg border border-[rgb(var(--border))] px-2 text-[11px] font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.12)]"
                            aria-label={`Quitar rango ${index + 1}`}
                          >
                            Quitar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {renderFieldError('disponibilidadHoraria')}
                </div>
              </div>
            </div>
          </section>

          <StickyActions
            onCancel={() => {
              resetForm();
              setActiveTab('listado');
            }}
            onSubmit={() => handleSubmit('back-to-list')}
            isSubmitting={isSaving}
            disablePrimary={isLoadingDetail}
            primaryLabel={selectedRetiroId ? 'Actualizar retiro' : 'Guardar retiro'}
            primaryBusyLabel="Guardando..."
            secondaryActions={selectedRetiroId ? [] : [{
              label: 'Guardar y crear otro',
              onClick: () => handleSubmit('create-another'),
              disabled: isLoadingDetail,
            }]}
          />
        </div>
      )}

      <CustomerModal
        isOpen={Boolean(modalTarget)}
        onClose={() => setModalTarget(null)}
        onSelect={customer => {
          if (modalTarget) {
            applyCustomer(modalTarget, customer);
          }
          setModalTarget(null);
        }}
      />
    </div>
  );
}

